import { Alert, Snackbar, Stack, Typography } from '@mui/material';
import { isErrorResponse } from '@ts-rest/core';
import type { FC } from 'react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from '~/components/atoms/Link';
import { ResetPasswordForm } from '~/components/organisms/ResetPasswordForm';
import { LOGIN_PATH } from '~/constants';
import { Logger } from '~/lib/logger';
import { useApi } from '~/providers/ApiProvider';
import type { ResetPasswordFormType } from '~/types/ResetPassword';

const logger = new Logger('ForgotPassword');

export const ResetPassword: FC = () => {
  const { warehouseApi } = useApi();
  const [searchParams] = useSearchParams();
  const payload = decodeURI(searchParams.get('payload') || '');

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const { mutate, isPending } = warehouseApi.resetPassword.useMutation({
    onSuccess: () => {
      setShowSuccess(true);
    },
    onError: (error) => {
      if (isErrorResponse(error)) {
        setErrorMessage(error.body.error.message);
      } else {
        setErrorMessage('Unexpected error occurred');
      }

      logger.error('Error resetting password', error);
    },
  });

  const onSubmit = (data: ResetPasswordFormType) => {
    if (!payload) {
      logger.error('Form submit error: payload not found in URL');
      return;
    }

    mutate({
      body: {
        data: {
          payload,
          // Dummy email required for Cloudflare's leaked credential detection to work
          email: 'supplier-portal@montu.com.au',
          password: data.password,
        },
      },
    });
  };

  return (
    <>
      <Stack spacing={4.75} maxWidth={360} alignSelf="center">
        <Typography component="h1" variant="subtitle1" fontWeight={600} textAlign="center">
          Reset Password
        </Typography>

        {showSuccess ? (
          <Typography
            variant="subtitle2"
            fontWeight={600}
            textAlign="center"
            sx={({ palette }) => ({ color: palette.success.main })}
          >
            Return to the login page to sign in with your new password.
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle2" fontWeight={600} textAlign="center">
              Enter your new password below and confirm it to reset your password.
            </Typography>

            <ResetPasswordForm isPending={isPending} onSubmit={onSubmit} />
          </>
        )}

        <Typography
          id="reset_password_back_to_login_link"
          component={Link}
          to={LOGIN_PATH}
          variant="body2"
          fontWeight={600}
          textAlign="center"
        >
          Back to Login
        </Typography>
      </Stack>

      <Snackbar open={!!errorMessage} autoHideDuration={4000} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          severity="error"
          variant="standard"
          title="Error resetting password"
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
